import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useFilterContext } from './filtercontext';
import { parseISO } from 'date-fns';
import { formatDate } from '../../../utils/formatters';

// Convert an ISO date string (YYYY-MM-DD or full ISO) into a Date.
// Returns null if the input is missing or invalid so callers can pass through
// optional seed props without extra guards.
function seedToDate(seed) {
  if (!seed) return null;
  if (seed instanceof Date) return isNaN(seed) ? null : seed;
  try {
    const d = parseISO(seed);
    return isNaN(d) ? null : d;
  } catch {
    return null;
  }
}

export default function DateRangeFilter({
  field,
  label = "Date Range",
  getDateValue = item => parseISO(item[field]),
  startLabel = "From",
  endLabel = "To",
  // Optional seed values (ISO date strings or Date objects). Used by URL-driven
  // drill-downs (e.g., from a DRFA row) so the filter mounts pre-applied.
  initialStartDate = null,
  initialEndDate = null,
}) {
  const { originalData, registerFilter, clearFilter } = useFilterContext();

  // States for start and end dates — seeded from props on first render only.
  const [startDate, setStartDate] = useState(() => seedToDate(initialStartDate));
  const [endDate, setEndDate] = useState(() => seedToDate(initialEndDate));

  // States for data date range
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  // Track initialization and prevent loops
  const [initialized, setInitialized] = useState(false);
  const originalDataCacheRef = useRef(null);
  const hasFilterRef = useRef(false);
  const cachedDateRangeRef = useRef({ minDate: null, maxDate: null });
  const currentDatesRef = useRef({ startDate: null, endDate: null });

  // Calculate and update date range when originalData changes (only when truly new data comes in)
  useEffect(() => {
    // Only recalculate if originalData reference actually changed or we haven't initialized
    if (originalDataCacheRef.current === originalData && initialized) {
      return;
    }

    originalDataCacheRef.current = originalData;

    if (!originalData || !originalData.length) {
      if (!initialized) {
        setMinDate(null);
        setMaxDate(null);
        cachedDateRangeRef.current = { minDate: null, maxDate: null };
        setInitialized(true);
      }
      return;
    }

    const dates = originalData
      .map(getDateValue)
      .filter(date => date instanceof Date && !isNaN(date));

    if (!dates.length) {
      if (!initialized) {
        setMinDate(null);
        setMaxDate(null);
        cachedDateRangeRef.current = { minDate: null, maxDate: null };
        setInitialized(true);
      }
      return;
    }

    const newMinDate = new Date(Math.min(...dates));
    const newMaxDate = new Date(Math.max(...dates));

    // Only update if dates actually changed or not initialized
    if (!cachedDateRangeRef.current.minDate ||
      !cachedDateRangeRef.current.maxDate ||
      newMinDate.getTime() !== cachedDateRangeRef.current.minDate.getTime() ||
      newMaxDate.getTime() !== cachedDateRangeRef.current.maxDate.getTime() ||
      !initialized) {

      setMinDate(newMinDate);
      setMaxDate(newMaxDate);
      cachedDateRangeRef.current = { minDate: newMinDate, maxDate: newMaxDate };
      setInitialized(true);
    }
  }, [originalData, getDateValue, initialized]);

  // Create clear handler function
  const handleClearFromContext = () => {
    setStartDate(null);
    setEndDate(null);
    currentDatesRef.current = { startDate: null, endDate: null };
    hasFilterRef.current = false;
  };

  // Update filter when date range changes
  useEffect(() => {
    if (!initialized) return;

    const shouldHaveFilter = startDate || endDate;

    // Check if dates actually changed
    const datesChanged =
      currentDatesRef.current.startDate !== startDate ||
      currentDatesRef.current.endDate !== endDate;

    if (shouldHaveFilter && (datesChanged || !hasFilterRef.current)) {
      const filterFn = item => {
        const itemDate = getDateValue(item);

        // Skip items with invalid dates
        if (!(itemDate instanceof Date) || isNaN(itemDate)) return false;

        // Check start date if set
        if (startDate && itemDate < startDate) return false;

        // Check end date if set
        if (endDate) {
          // Set end date to end of day
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (itemDate > endOfDay) return false;
        }

        return true;
      };

      const handleFilterDescription = () => {
        if (!startDate && !endDate) return `${label}: (no filter)`;
        if (startDate && !endDate) {
          return `${label}: From ${formatDate(startDate)}`;
        }
        if (!startDate && endDate) {
          return `${label}: Up to ${formatDate(endDate)}`;
        }
        return `${label}: ${formatDate(startDate)} - ${formatDate(endDate)}`;
      }

      console.log(`Registering date filter for field "${field}" with dates:`, { startDate, endDate });
      registerFilter(field, label, filterFn, handleClearFromContext, handleFilterDescription);
      hasFilterRef.current = true;
      currentDatesRef.current = { startDate, endDate };
    } else if (!shouldHaveFilter && hasFilterRef.current) {
      console.log(`Clearing date filter for field "${field}"`);
      clearFilter(field);
      hasFilterRef.current = false;
      currentDatesRef.current = { startDate: null, endDate: null };
    }
  }, [startDate, endDate, field, getDateValue, registerFilter, clearFilter, initialized]);

  if (!initialized) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <DatePicker
            label={startLabel}
            value={startDate}
            onChange={setStartDate}
            minDate={minDate}
            maxDate={endDate || maxDate}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DatePicker
            label={endLabel}
            value={endDate}
            onChange={setEndDate}
            minDate={startDate || minDate}
            maxDate={maxDate}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}